import React, { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  CheckCircle,
  Award,
  Type,
} from 'lucide-react';
import ScratchStemEditor from './ScratchStemEditor';
import ScratchReferenceAnswer from './ScratchReferenceAnswer';
import ScratchScoringRules from './ScratchScoringRules';

interface Question {
  id: string;
  type: string;
  title: string;
  score: number;
  content?: string;
  referenceAnswer?: string;
  scoringPoints?: string;
  maxTextLength?: number;
  minTextLength?: number;
  allowTextInput?: boolean;
  gradingMode?: 'manual' | 'ai';
  titleImageWidths?: number[];
  referenceAnswerImageWidths?: number[];
  scoringPointsImageWidths?: number[];
  [key: string]: any;
}

interface SubjectiveQuestionEditorProps {
  question: Question;
  paperId?: string;
  editSessionId: number;
  onUpdateQuestion: (id: string, data: Partial<Question>) => void;
}

// 字符限制快捷预设
const CHAR_PRESETS = [
  { label: '不限', max: 0, min: 0 },
  { label: '100字', max: 100, min: 0 },
  { label: '200字', max: 200, min: 0 },
  { label: '500字', max: 500, min: 0 },
  { label: '1000字', max: 1000, min: 0 },
  { label: '2000字', max: 2000, min: 0 },
];

export default function SubjectiveQuestionEditor({
  question,
  editSessionId,
  onUpdateQuestion,
}: SubjectiveQuestionEditorProps) {
  // 折叠状态管理（仅 UI 状态，与数据无关）
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    stem: true,
    answer: true,
    reference: true,
    rules: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // 字符限制输入框本地值（受控输入框显示，避免输入过程中数字跳动）
  const maxCharsInput = question.maxTextLength ? String(question.maxTextLength) : '';
  const minCharsInput = question.minTextLength ? String(question.minTextLength) : '';

  const handleMaxCharsChange = (value: string) => {
    const num = parseInt(value) || 0;
    onUpdateQuestion(question.id, { maxTextLength: num });
  };

  const handleMinCharsChange = (value: string) => {
    const num = parseInt(value) || 0;
    onUpdateQuestion(question.id, { minTextLength: num });
  };

  const applyPreset = (max: number, min: number) => {
    onUpdateQuestion(question.id, { maxTextLength: max, minTextLength: min });
  };

  return (
    <div className="space-y-4">
      {/* 1. 题干区 - 直接渲染编辑器，参照客观题极简风格 */}
      <ScratchStemEditor
        key={`stem-${question.id}-${editSessionId}`}
        value={question.content || ''}
        onChange={(content, widths) => {
          onUpdateQuestion(question.id, { content });
          if (widths && widths.length > 0) {
            onUpdateQuestion(question.id, { titleImageWidths: widths });
          }
        }}
        questionId={question.id}
        imageWidths={question.titleImageWidths || []}
      />

      {/* 2. 作答设置区 - 字符数量限制配置 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('answer')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="font-medium text-gray-900">作答配置</span>
          </div>
          {expandedSections.answer ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.answer && (
          <div className="p-4 space-y-5">
            {/* 答题方式说明 */}
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <Type className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="text-sm text-emerald-700">
                学生通过文本输入框作答，可配置字数限制
              </span>
            </div>

            {/* 字数限制配置 */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700">字数限制</div>

              {/* 快捷预设 */}
              <div className="flex flex-wrap gap-2">
                {CHAR_PRESETS.map(preset => {
                  const currentMax = question.maxTextLength || 0;
                  const currentMin = question.minTextLength || 0;
                  const isActive = currentMax === preset.max && currentMin === preset.min;
                  return (
                    <button
                      key={preset.label}
                      onClick={() => applyPreset(preset.max, preset.min)}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-colors border ${
                        isActive
                          ? 'bg-emerald-500 text-white border-emerald-400 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-emerald-50 hover:border-emerald-300'
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>

              {/* 自定义字数输入 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">最小字数</label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      value={minCharsInput}
                      onChange={(e) => handleMinCharsChange(e.target.value)}
                      placeholder="不限制"
                      className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">字</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">最大字数</label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      value={maxCharsInput}
                      onChange={(e) => handleMaxCharsChange(e.target.value)}
                      placeholder="不限制"
                      className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">字</span>
                  </div>
                </div>
              </div>

              {/* 配置预览 */}
              <div className="px-3 py-2 bg-gray-50 rounded-lg">
                <span className="text-xs text-gray-500">当前规则：</span>
                <span className="text-xs text-gray-700 font-medium">
                  {(() => {
                    const max = question.maxTextLength || 0;
                    const min = question.minTextLength || 0;
                    if (max === 0 && min === 0) return '不限字数';
                    if (max === 0) return `至少 ${min} 字`;
                    if (min === 0) return `最多 ${max} 字`;
                    return `${min} - ${max} 字`;
                  })()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. 参考答案区 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('reference')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-900">参考答案</span>
          </div>
          {expandedSections.reference ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.reference && (
          <div className="p-4">
            <ScratchReferenceAnswer
              key={`ref-answer-${question.id}-${editSessionId}`}
              showProgramDownload={false}
              answerText={question.referenceAnswer || ''}
              onAnswerTextChange={(text, widths) => {
                onUpdateQuestion(question.id, { referenceAnswer: text });
                if (widths && widths.length > 0) {
                  onUpdateQuestion(question.id, { referenceAnswerImageWidths: widths });
                }
              }}
              questionId={question.id}
              answerImageWidths={question.referenceAnswerImageWidths || []}
            />
          </div>
        )}
      </div>

      {/* 4. 评分规则区 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('rules')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-gray-900">评分规则</span>
          </div>
          {expandedSections.rules ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.rules && (
          <div className="p-4">
            <ScratchScoringRules
              key={`scoring-${question.id}-${editSessionId}`}
              value={question.scoringPoints || ''}
              onChange={(text, widths) => {
                onUpdateQuestion(question.id, { scoringPoints: text });
                if (widths && widths.length > 0) {
                  onUpdateQuestion(question.id, { scoringPointsImageWidths: widths });
                }
              }}
              questionId={question.id}
              imageWidths={question.scoringPointsImageWidths || []}
            />
          </div>
        )}
      </div>
    </div>
  );
}
